import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import '../assets/css/mission.css';
import '../assets/css/psearch.css'; 
import '../assets/css/about.css';
import ReactGA from 'react-ga';
import kasheer from '../assets/images/kasheer.png';
import SignInModal from './SignInModal';
import Nav from './Nav';
import SEO from './SEO';

ReactGA.initialize('G-SW7M3XVNPW'); 
ReactGA.pageview(window.location.pathname + window.location.search);

function CombinedPage() {
  const [user, setUser] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalMessage, setSignInModalMessage] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    getSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        console.error('Error signing in:', error.message);
        setAuthMessage(`Sign in error: ${error.message}`);
      } else {
        console.log('OAuth initiated:', data);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setAuthMessage(`Unexpected error: ${err.message}`);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!authEmail) return;
    
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithOtp({
      email: authEmail,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email for the magic link!');
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignIn = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setShowSignInModal(false);
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignUp = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { user_role: 'general' },
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email to confirm your account!');
    }
    setAuthLoading(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setShowUserDropdown(false);
  };

  return (
    <div className="mission-page">
      <SEO
        title="Our Vision — Preserving Kashmiri Fashion Heritage"
        description="Yemberzal's mission is to preserve and promote Kashmir's fashion heritage. We connect Kashmiri artisans with the world through technology, culture, and craftsmanship."
        path="/vision"
      />
      <div className="container mission-shell">
      <Nav />

      <section className="mission-hero">
        <div className="mission-hero-media" aria-hidden="true">
          <img
            src={kasheer}
            alt=""
            className="mission-hero-image"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = '/default-image.avif';
            }}
          />
          <div className="mission-hero-overlay" />
        </div>
        <div className="mission-hero-content">
          {/* <h1 className="mission-kashmiri">یمبرزل </h1> */}
          <div className="mission-hero-titleRow">
            <p className="font-color-white font-size-52">Why</p>
            <h1 className="mission-title font-color-white">یمبرزل </h1>
          </div>
          <p className="mission-subtitle">
            A fashion search experience rooted in Kashmir, connecting heritage craft with modern discovery.
          </p>
          <div className="mission-hero-cta">
            <a className="mission-cta" href="/brands">Explore brands</a>
            <a className="mission-cta secondary" href="/">Search products</a>
          </div>
        </div>
      </section>

      <section className="mission-grid">
        {/* Vogue-style Editorial Header */}
        <div className="vogue-editorial-header">
          <h1 className="vogue-main-title">THE YEMBERZAL STORY</h1>
          <div className="vogue-subtitle">Kashmir's Fashion Renaissance</div>
        </div>

        {/* Editorial Content */}
        <article className="vogue-editorial-article">
          <div className="vogue-drop-cap">
            <span className="drop-cap">Y</span>
            <span className="drop-cap-text">emberzal - the Kashmiri word for narcissus flower, blooms in the harshest winter conditions, symbolizing resilience and hope. This same spirit lives in Kashmir's artisans, whose timeless craftsmanship thrives amid adversity.</span>
          </div>
          
          <div className="vogue-pull-quote">
            <div className="quote-mark">"</div>
            <p className="quote-text">Bridging Kashmir with the world through fashion and technology</p>
          </div>

          <div className="vogue-content-section">
            <h3 className="section-heading">WHY YEMBERZAL?</h3>
            <p className="editorial-text">
              The platform exists as a global stage for Kashmiri fashion brands making it easier to discover, support, and celebrate the creators crafting from the valley. We protect authenticity by centering real Kashmiri voices, ensuring our fashion is represented by Kashmiris, not diluted or misrepresented.
            </p>
            <p className="editorial-text">
              We bring the fashion stories of Kashmir to the world, amplifying the artisans behind every piece whose craft is often unseen, undervalued, and underpaid.
            </p>
          </div>

          <div className="vogue-divider"></div>

          <div className="vogue-content-section">
            <h3 className="section-heading">THE MISSION</h3>
            <p className="editorial-text">
              Yemberzal was born from frustration: authentic Kashmiri brands and artisans shouldn't be buried under endless pages of generic search results. I'm bridging the gap between fashion, Kashmir, and technology building a place where Kashmiri fashion can be found, understood, and respected.
            </p>
            <p className="editorial-text">
              This is built by a Kashmiri, for Kashmiris and for anyone in the world who wants to discover Kashmir through real brands, real makers, and real stories.
            </p>
          </div>

          <div className="vogue-signature">
            <div className="signature-line"></div>
            <p className="signature-text">My Love Letter to Kashmir 💌</p>
          </div>
        </article>

       
      </section>

      {/* Footer Section with disclaimer */}
      {/* <footer className="mission-footer">
        <cite className="mission-disclaimer">
          Disclaimer: I do not make money off of this, nor do I sell any products.
          <br />
          YEMBERZAL is simply a search engine to make finding Kashmiri Fashion seamless.
        </cite>
      </footer> */}

      </div>

      <SignInModal
        open={showSignInModal}
        onClose={() => {
          setShowSignInModal(false);
          setAuthMessage('');
          setAuthEmail('');
          setSignInModalMessage('');
        }}
        message={signInModalMessage}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authLoading={authLoading}
        authMessage={authMessage}
        onEmailSignIn={handleEmailSignIn}
        onGoogleSignIn={handleGoogleSignIn}
        onEmailPasswordSignIn={handleEmailPasswordSignIn}
        onEmailPasswordSignUp={handleEmailPasswordSignUp}
      />
    </div>
  );
}

export default CombinedPage;
